import { Injectable } from '@angular/core';
import { AwakeService } from '../awake.service';
import { PusherUserService } from '../landing/pusher-user.service';
import { distinctUntilChanged, filter, map, merge, Observable, of, switchMap, tap } from 'rxjs';
import { StoreWalletService } from '../store/store-wallet.service';
import { WalletService } from './wallet.service';
import { IBalancesList, IWallet, WALLET_CODE, WALLET_TYPE } from '@libs/interfaces';
import { ApiService } from '../api.service';
import { MapResponseData } from '@libs/rxjs/operators.rxjs';
import { orderBy } from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class BalanceUserService {
  constructor(
    private pusher: PusherUserService,
    private awakeService: AwakeService,
    private storeWalletService: StoreWalletService,
    private walletService: WalletService,
    private apiService: ApiService,
  ) {}
  /**
   * Initializes the WalletService by subscribing to wallet updates and fetching the selected balance.
   */
  init() {
    this.storeWalletService
      .selectWallet()
      .pipe(
        filter(Boolean),
        distinctUntilChanged(),
        tap(
          (wallet: IWallet) =>
            this.walletService.wallet()?.code != wallet.code && this.walletService.wallet.set(wallet),
        ),
        switchMap((wallet: IWallet) =>
          merge(
            of(true),
            this.pusher.updatedBalance,
            this.pusher.depositApproved,
            this.pusher.depositCryptoReceived,
            this.pusher.withdrawApproved,
            this.pusher.winnersBet,
            this.awakeService.awake$,
          ).pipe(map((w: IWallet) => wallet)),
        ),
        switchMap((w: IWallet) => this.fetchSelectedBalance(w)),
      )
      .subscribe();
  }
  /**
   * Fetches the balances for the current wallet.
   * @returns {Observable<IBalancesList>} An observable containing the balances list.
   */
  fetchBalances() {
    return this.fetchAllBalances().pipe(
      map(balances => this.parse(balances)),
      map((balances: IBalancesList) => this.setElementsToSee(balances)),
    );
  }

  /**
   * Fetches all balances.
   * @returns {Observable<any>} An observable containing all balances.
   */
  private fetchAllBalances() {
    return this.apiService.get('user/balances').pipe(MapResponseData);
  }

  /**
   * Sets the elements to see in the balances list.
   * @param {IBalancesList} balances - The balances list.
   * @returns {IBalancesList} The updated balances list.
   */
  private setElementsToSee(balances: IBalancesList): IBalancesList {
    const selectedIsFiat = balances.wallet.type == 'fiat';
    const numElements = 5;
    let isInArray = selectedIsFiat;
    balances.balances_crypto_show = balances.balances_crypto.reduce((acum, ele) => {
      if (acum.length + 1 <= numElements || !isInArray) {
        acum = [...acum, ele];
        ele.wallet.code == balances.wallet.code && (isInArray = true);
      }
      return acum;
    }, []);
    balances.all = balances.balances_crypto_show.length == balances.balances_crypto.length;
    return balances;
  }

  /**
   * Parses the wallets and balances to create a balances list.
   * @param {any[]} balances - The balances.
   * @returns {IBalancesList} The parsed balances list.
   */
  private parse(balances): IBalancesList {
    const _wallets = orderBy(
      balances.map((w: { code: WALLET_CODE; balance: number }) => ({
        wallet: this.walletService.wallets().find(_w => _w.code == w.code),
        balance: w.balance,
        type: this.walletService.wallet().type,
      })),
      ['balance'],
      ['desc'],
    ).filter(w => w.wallet);
    return {
      wallet: this.walletService.wallet(),
      balances_fiat: _wallets.filter(w => w.wallet.type == WALLET_TYPE.FIAT),
      balances_crypto: _wallets.filter(w => w.wallet.type == WALLET_TYPE.CRYPTO),
      balances_token: _wallets.filter(w => w.wallet.type == WALLET_TYPE.TOKEN),
      balances_crypto_show: [],
      wallets: this.walletService.wallets(),
      all: false,
    };
  }

  /**
   * Fetches the selected balance for the given wallet.
   * @param {IWallet} wallet - The wallet.
   * @returns {Observable<boolean>} An observable that completes when the balance is fetched.
   */
  fetchSelectedBalance(wallet: IWallet): Observable<boolean> {
    return this.apiService.get(`user/balance/${wallet.code}`).pipe(
      MapResponseData,
      tap((balance: number) => this.storeWalletService.updateBalance(balance)),
      map(() => true),
    );
  }
}
